import _ from 'lodash';
import qs from 'qs';

import api from '../../../mdms_api';
import cms_api from '../../../cms_api';
import { digChoicesFromData } from '../../../components/Builder/Library/AttributeSet';
import { unwrapRef } from '../../helpers';

const dataProviderV1 = async (ref) => {
  const url = `/api/v1/cms/sites/${PB_SITE}/data`;
  const queryHash = _.cloneDeep(ref);
  if (typeof(window) !== 'undefined' && typeof(window.PB_ZONE) !== 'undefined') {
    queryHash.zone = window.PB_ZONE;
  }
  const query = qs.stringify(queryHash, { arrayFormat: 'brackets', encodeValuesOnly: true })

  const response = await api.get(`${ url }?${ query }`)
  if (response.status === 200) {
    return await response.json();
  }
  if (response.status === 204) {
    return Promise.resolve({ meta: { parameters: ref.parameters }, data: null });
  }

  console.error(`Error retrieving data: ${response.status}`, response)
  return Promise.resolve({ meta: { parameters: ref.parameters }, data: null });
};

const dataProviderV2 = async (ref) => {
  const {
    dataset,
    parameters: { [dataset]: parameters } = {},
    ...rest
  } = ref
  const queryHash = { dataset, parameters, ...rest }

  return { meta: {}, data: await cms_api.get_all(queryHash, {}, 'data') }
};

const getBaseQuery = (dataset, language) => {
  const type = {
    document_builder: 'Document',
    insulation_applications_blog: 'Article::Applications',
    insulation_industrial_blog: 'Article::InsulationIndustrial',
    insulation_residential_blog: 'Article::InsulationResidential',
    iss: 'Article::InsulationSolutionsSuite',
    lumber_blog: 'Article::Lumber',
    paroc_articles: 'Article::Paroc',
    paroc_structure_selector: 'Document',
    products_enclosure: 'Product::Enclosure',
    products_fleetwood: 'Product::Fleetwood',
    products_insulation: 'Product::Insulation',
    products_paroc: 'Product::Paroc',
    products_roofing: 'Product::Roofing',
    roofing_blog: 'Article::Roofing',
    newsroom_blog: 'Article::Newsroom',
    fleetwood_blog: 'Article::Fleetwood',
    products_pfc: 'Product::PfcSelector',
  }[dataset];
  return {
    filter: {
      type: `Cms::Content::${type}`,
      language_iso_code: language,
      'metadata.redirect': 'null',
    },
    fields: { '*': 'contents,metadata,language_iso_code,content_uuid,route,start_at' },
  };
};

const getArticleQuery = (dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast) => {
  const metadata = { 'settings.availability.visibility': 'listed', };

  return _.merge(
    getBaseQuery(dataset, language),
    {
      filter: {
        metadata: {
          ...metadata,
          ...(excludeEmptyPublishDates
            ? { 'settings.details.information.published': '\x00' }
            : {})
        }
      },
      sort: '-metadata.settings.details.information.published',
      ...(nullPubDatesSortLast ? { nulls_last: true } : {}),
    }
  );
}

const getDocumentQuery = (dataset, language) =>
  _.merge(
    _.omit(getBaseQuery(dataset, language), 'filter.language_iso_code'),
    {
      page: { number: 1, size: 1000 },
      sort: 'metadata.settings.attributes.title',
      sort_with_lower: true,
    }
  );

const getParocStructureSelectorQuery = (dataset, lang) =>
  _.merge(_.omit(getBaseQuery(dataset, lang), 'filter.language_iso_code'), {
    filter: {
      'metadata.settings.attributes.document_type': 'cad-drawing',
      language_iso_code: [lang, lang.split('-')[0]],
    },
    page: { number: 1, size: 1000 },
    sort: 'metadata.settings.attributes.title',
    sort_with_lower: true,
  });

const getProductQuery = (dataset, language) => {
   const metadata = { 'settings.availability.visibility': 'listed', };

  if (dataset === 'products_roofing' && window.PB_ZONE && window.PB_ZONE !== 'all') {
    metadata['settings.availability.zones'] = [window.PB_ZONE];
  }

  return _.merge(
    getBaseQuery(dataset, language),
    {
      filter: {
        metadata: metadata,
      },
      sort: 'metadata.settings.general.proper_name',
      sort_with_lower: true,
    }
  );
}

const filterAggPromise = async (prop, query, itemAttributes) => {
  const data = await cms_api.get_single_for_language(query, Board.build.language)
  const hasFilters = !!data;
  const response = {
    filters: hasFilters ? await digChoicesFromData(prop, data) : null,
  };
  if (itemAttributes && itemAttributes.length > 0 && data?.contents?.items?.length > 0) {
    const attributeFields = data.contents.items.filter(i => itemAttributes.includes(i.field));

    response.attributes = attributeFields.map(attributeField => {
      const { label, type, field } = attributeField;
      const fieldName = `${prop}.${field}`;
      const filter = response.filters[fieldName];
      return {
        label,
        field: fieldName,
        type,
        ...(type === 'choices' && filter?.of ? { of: remapOptionsByValue(filter.of) } : {}),
      };
    });
  }
  return response;
};

const remapOptionByValue = (remappedByValue, option, parent, depth) => {
  remappedByValue[option.value] = { ...option, depth, parent };
  if (option.children) {
    option.children.forEach(c => remapOptionByValue(remappedByValue, c, remappedByValue[option.value], depth+1))
  }
}

// create a lookup of value to option, including a parent traversal
const remapOptionsByValue = (options) => {
  const remappedByValue = {};
  options?.forEach(c => remapOptionByValue(remappedByValue, c, null, 1));
  return remappedByValue;
}

const dataProviderRoutesV2 = async (ref, usePagination = false) => {
  //var startTime = performance.now()

  const { dataset, language = typeof Board !== 'undefined' ? Board.language : null, parameters = {} } = ref;
  const { excludeEmptyPublishDates, nullPubDatesSortLast } = parameters;

  const query = {
    document_builder: getDocumentQuery(dataset, language),
    insulation_applications_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    insulation_industrial_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    insulation_residential_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    iss: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    lumber_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    paroc_articles: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    paroc_structure_selector: getParocStructureSelectorQuery(dataset, language),
    products_enclosure: getProductQuery(dataset, language),
    products_fleetwood: getProductQuery(dataset, language),
    products_insulation: getProductQuery(dataset, language),
    products_paroc: getProductQuery(dataset, language),
    products_pfc: getProductQuery(dataset, language),
    products_roofing: getProductQuery(dataset, language),
    roofing_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    newsroom_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
    fleetwood_blog: getArticleQuery(dataset, language, excludeEmptyPublishDates, nullPubDatesSortLast),
  }[dataset];
  const source = parameters.source || parameters.productSource || 'all';
  const promises = [];
  switch (source) {
    case 'specific': {
      const { items = [] } = parameters;
      promises.push(Promise.all(items.filter(item => item?.product || item?.item).map(async item => {
        // TODO: restructure helpers so we can use expandRef instead
        const [type, params, data] = unwrapRef(item.product || item.item);
        if (data) { return Promise.resolve(data); }
        return cms_api.get_single_for_language({
          filter: {
            content_uuid: params.id,
            ...(params.type ? { type: `Cms::Content::${params.type}` } : {}),
          },
          fields: {
            '*': 'content_uuid,name,contents,metadata,language_iso_code,route,start_at'
          },
        }, Board.build.language);
      })))
      break;
    }
    case 'all':
    case 'filtered': {
      const { limit = false, limitCount = 10 } = parameters;
      if (limit) {
        query.page = { number: 1, size: limitCount };
      }
      if (source === 'filtered') {
        const { filters } = parameters;
        Object.keys(filters).forEach(filter => {
          query.filter[filter] = filters[filter];
        });
      }
      if (usePagination) {
        promises.push(cms_api.get_all(query));
      } else {
        promises.push(cms_api.get(query));
      }
    }
  }

  const { showFilters, enabledFilters, itemAttributes = [] } = parameters;
  // TEMP!  PB-945 - migrate in old doc builders
  if (
    (dataset === 'document_builder' || dataset === 'paroc_structure_selector') &&
    typeof parameters.itemAttributes === 'undefined'
  ) {
    itemAttributes.push('country', 'language', 'document_type');
  }
  // END TEMP!  PB-945 - migrate in old doc builders
  const retrieveAttributeSet = showFilters || itemAttributes.length > 0;
  if (retrieveAttributeSet) {
    const attributeSetInfo = {
      document_builder: {
        prop: 'metadata.settings.attributes',
        name: 'Document Attributes',
        route: '/',
      },
      insulation_applications_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Applications Attributes',
        route: '/insulation',
      },
      insulation_industrial_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Blog - Foamglas',
        route: '/insulation',
      },
      insulation_residential_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Blog - Insulation Homeowner',
        route: '/insulation',
      },
      iss: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'ISS Article Attributes',
        route: '/insulation/insulation-solutions-suite/article',
      },
      lumber_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Blog - Lumber',
        route: '/composites/lumber',
      },
      paroc_articles: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Article Attributes',
        route: '/',
      },
      paroc_structure_selector: {
        prop: 'metadata.settings.attributes',
        name: 'Document Attributes',
        route: '/',
      },
      products_enclosure: {
        prop: 'metadata.settings.attributes',
        name: 'Enclosure Solutions',
        route: '/insulation/commercial/enclosure'
      },
      products_fleetwood: {
        prop: 'metadata.settings.attributes',
        name: 'Product Attributes',
        route: '/',
      },
      products_insulation: {
        prop: 'metadata.settings.attributes',
        name: 'Insulation PDP Attributes',
        route: '/insulation',
      },
      products_paroc: {
        prop: 'metadata.settings.attributes',
        name: 'Product Attributes',
        route: '/',
      },
      products_roofing: {
        prop: 'metadata.settings.attributes',
        name: 'Roofing PDP Attributes',
        route: '/roofing',
      },
      roofing_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Roofing Article Attributes',
        route: '/roofing/blog',
      },
      newsroom_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Corporate Newsroom',
        route: '/news/news-releases',
      },
      fleetwood_blog: {
        prop: 'metadata.settings.details.taxonomies',
        name: 'Article Attributes',
        route: '/blog',
      },
      products_pfc: {
        prop: 'metadata.settings.attributes',
        name: 'PFC Selector Attributes',
        route: '/',
      },
    }[dataset];
    const { prop, ...attributeSet } = attributeSetInfo;
    const filterQuery = {
      filter: {
        type: 'Cms::Content::AttributeSet',
        ...attributeSet,
      },
      fields: {
        '*': 'contents,metadata',
      },
    };
    promises.push(filterAggPromise(prop, filterQuery, itemAttributes));
  } else {
    promises.push(Promise.resolve(null));
  }

  const results = await Promise.all(promises);
  const meta = { parameters };
  if (retrieveAttributeSet) {
    // insulation_applications_blog is actually a PDP we treat like article
    const pickedFilters = dataset === 'insulation_applications_blog' ? enabledFilters.enabledFilters.map(f => f.replace(/metadata\.settings\.attributes/, 'metadata.settings.details.taxonomies')) : enabledFilters.enabledFilters
    const { filters, attributes } = results[1];
    meta.filters = _.pick(filters, pickedFilters);
    meta.attributes = attributes;
  }

  //var endTime = performance.now()
  //console.log(`Call to dataProviderRoutesV2 took ${endTime - startTime} milliseconds`)

  return { meta, data: results[0] };
};

export default async (ref) => {
  const { dataset } = ref;
  switch (dataset) {
    case 'document_builder':
    case 'insulation_applications_blog':
    case 'insulation_industrial_blog':
    case 'insulation_residential_blog':
    case 'iss':
    case 'lumber_blog':
    case 'paroc_articles':
    case 'paroc_structure_selector':
    case 'products_roofing':
    case 'roofing_blog':
    case 'newsroom_blog':
    case 'fleetwood_blog':
    case 'products_pfc':
      return dataProviderRoutesV2(ref, false);
    case 'products_insulation':
    case 'products_fleetwood':
    case 'products_enclosure':
    case 'products_paroc':
      return dataProviderRoutesV2(ref, true)
    case 'color_pairing':
      return dataProviderV2(ref)
    default:  // legacy, shingles, bim, bim_base_items, bim_product_groups, bim_product_group
      return dataProviderV1(ref);
  }
};
